'use client';

import type { ChangeEvent, FocusEvent, KeyboardEvent, ReactNode } from 'react';
import { useEffect, useId, useRef, useState } from 'react';
import { Input } from '@savvy-kit/components/input';
import { Tooltip, TooltipContent, TooltipTrigger } from '@savvy-kit/components/tooltip';
import { cn } from '@savvy-kit/lib/utils';
import { Check } from 'lucide-react';
import { isValidEmail, isValidPhone } from 'src/features/advisor-book-onboarding/utils/staged-book';

type EditableContactField = 'email' | 'phone' | 'text';

type EditableContactCellProps = {
  readonly value: string;
  readonly field: EditableContactField;
  readonly onCommit: (value: string) => void;
  readonly disabled?: boolean;
  readonly saving?: boolean;
  readonly ariaLabel?: string;
  /** Shown in read mode only; edit/commit still use `value`. */
  readonly displayValue?: string;
  /** Associates a visible `<Label htmlFor>` with this control. */
  readonly id?: string;
  /** Server/import validation message shown when the field has an error. */
  readonly errorMessage?: string;
  /** When set with disabled, explains why the field is not editable. */
  readonly disabledReason?: string;
};

function isFieldValid(field: EditableContactField, value: string): boolean {
  if (field === 'text') {
    return true;
  }
  return field === 'email' ? isValidEmail(value) : isValidPhone(value);
}

function inputTypeForField(field: EditableContactField): 'email' | 'tel' | 'text' {
  if (field === 'email') {
    return 'email';
  }
  if (field === 'phone') {
    return 'tel';
  }
  return 'text';
}

function renderDisplayContent({
  hasValue,
  validity,
  value,
}: {
  readonly hasValue: boolean;
  readonly validity: boolean;
  readonly value: string;
}) {
  if (!hasValue) {
    return <span className="text-destructive">Missing</span>;
  }
  if (validity) {
    return <span className="truncate text-dark">{value}</span>;
  }
  return <span className="truncate text-destructive">{value}</span>;
}

function maybeWrapWithDisabledReason({
  disabled,
  disabledReason,
  children,
}: {
  readonly disabled: boolean;
  readonly disabledReason: string | undefined;
  readonly children: ReactNode;
}) {
  if (!disabled || disabledReason === undefined || disabledReason.trim() === '') {
    return children;
  }

  return (
    <Tooltip>
      <TooltipTrigger render={<div className="w-full" />}>{children}</TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs text-left">
        {disabledReason}
      </TooltipContent>
    </Tooltip>
  );
}

export function EditableContactCell({
  value,
  field,
  onCommit,
  disabled = false,
  saving = false,
  ariaLabel,
  displayValue,
  id,
  errorMessage,
  disabledReason,
}: EditableContactCellProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const generatedErrorId = useId();
  const errorId = id === undefined ? generatedErrorId : `${id}-error`;
  const skipBlurRefocusRef = useRef(false);

  useEffect(() => {
    if (editing && inputRef.current !== null) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const trimmedValue = value.trim();
  const validity = isFieldValid(field, trimmedValue) && (errorMessage === undefined || errorMessage === '');
  const hasValue = trimmedValue.length > 0;
  const trimmedErrorMessage = errorMessage?.trim() ?? '';

  function exitEditMode({ restoreFocus }: { readonly restoreFocus: boolean }) {
    setEditing(false);
    if (!restoreFocus) {
      return;
    }
    requestAnimationFrame(() => {
      buttonRef.current?.focus();
    });
  }

  function startEdit() {
    if (disabled || saving) {
      return;
    }
    setDraft(value);
    setEditing(true);
  }

  function commit({ restoreFocus }: { readonly restoreFocus: boolean }) {
    const trimmedDraft = draft.trim();
    // Re-commit unchanged values when a server/import error is still showing so
    // callers can normalize (e.g. split a combined name into first + last).
    if (trimmedDraft !== trimmedValue || trimmedErrorMessage !== '') {
      onCommit(trimmedDraft);
    }
    exitEditMode({ restoreFocus });
  }

  function cancel() {
    setDraft(value);
    exitEditMode({ restoreFocus: true });
  }

  function handleBlur(_event: FocusEvent<HTMLInputElement>) {
    const restoreFocus = !skipBlurRefocusRef.current;
    skipBlurRefocusRef.current = false;
    commit({ restoreFocus });
  }

  if (editing) {
    const draftValid = isFieldValid(field, draft.trim());
    const showDraftError = field !== 'text' && !draftValid && draft.trim() !== '';
    return (
      <div className="flex flex-col gap-1">
        <Input
          id={id}
          ref={inputRef}
          type={inputTypeForField(field)}
          value={draft}
          aria-label={ariaLabel ?? `Edit ${field}`}
          aria-invalid={!draftValid}
          aria-describedby={showDraftError ? errorId : undefined}
          disabled={saving}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            setDraft(event.target.value);
          }}
          onBlur={handleBlur}
          onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Tab') {
              skipBlurRefocusRef.current = true;
              return;
            }
            if (event.key === 'Enter') {
              event.preventDefault();
              commit({ restoreFocus: true });
            }
            if (event.key === 'Escape') {
              event.preventDefault();
              cancel();
            }
          }}
          className="h-8"
        />
        {showDraftError && (
          <p id={errorId} className="text-xs text-destructive">
            Enter a valid {field}
          </p>
        )}
      </div>
    );
  }

  const showServerError = trimmedErrorMessage !== '';
  const baseAriaLabel = ariaLabel ?? `Edit ${field}${hasValue ? `: ${displayValue ?? value}` : ' (missing)'}`;
  const resolvedAriaLabel =
    disabled && disabledReason !== undefined && disabledReason.trim() !== ''
      ? `${baseAriaLabel}. ${disabledReason}`
      : baseAriaLabel;

  return maybeWrapWithDisabledReason({
    disabled,
    disabledReason,
    children: (
      <div className="flex flex-col gap-1">
        <button
          id={id}
          ref={buttonRef}
          type="button"
          onClick={startEdit}
          disabled={disabled || saving}
          aria-label={resolvedAriaLabel}
          aria-invalid={!validity || showServerError}
          aria-describedby={showServerError ? errorId : undefined}
          className={cn(
            'group flex w-full items-center gap-1.5 rounded-md text-left text-sm',
            !validity || showServerError
              ? 'border border-destructive bg-destructive/5 px-3 py-1.5 ring-2 ring-destructive/20'
              : 'px-1.5 -mx-1.5 py-1 hover:bg-muted',
            disabled || saving ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
          )}
        >
          {renderDisplayContent({ hasValue, validity, value: displayValue ?? value })}
          {validity && hasValue && (
            <Check className="size-3 shrink-0 text-primary/40 opacity-0 group-hover:opacity-100" aria-hidden="true" />
          )}
        </button>
        {showServerError && (
          <p id={errorId} className="text-xs text-destructive">
            {trimmedErrorMessage}
          </p>
        )}
      </div>
    ),
  });
}
